import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    AppNotificationService,
    AppNotificationType,
    ConfigService,
    LoadingScreenComponent,
    LoadingScreenModel,
    ModalClass,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { from, of } from 'rxjs';
import { catchError, exhaustMap, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { DocBoxService } from '../../services/docBox.service';
import {
    downloadDocuments,
    downloadDocumentsLoadingStatus,
    getDocBox,
    getDocBoxError,
    getDocBoxFromSideMenu,
    getDocBoxSuccess,
    getDocBoxFromSideMenuSuccess,
    navigateToDocBox,
} from '../actions/docBox.action';
import { BsModalService } from 'ngx-bootstrap/modal';

@Injectable({
    providedIn: 'root',
})
export class DocBoxEffectsService {
    public constructor(
        private actions$: Actions,
        private readonly configService: ConfigService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly router: Router,
        private readonly docBoxService: DocBoxService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
        private readonly modalService: BsModalService,
    ) {}

    public navigateToDocBox$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(navigateToDocBox),
                exhaustMap((action) => {
                    const { frontId, qmNum, maNum } = action.notificationId;
                    return from(this.router.navigate(['./main/docbox/', frontId, qmNum, maNum]));
                }),
            ),
        { dispatch: false },
    );

    public getDocBox$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getDocBox),
            switchMap((action) => {
                const { notificationId, materialNumber, serialNumber } = action;
                const loadingScreenModel = new LoadingScreenModel(
                    <string>this.translate.instant('NOTIFICATION_DETAILS_DETAIL_LINKS_DOC_BOX_LOADING_MESSAGE'),
                );

                const loadingScreenInstance = this.modalService.show(LoadingScreenComponent, {
                    backdrop: 'static',
                    class: ModalClass.MODAL_SMALL_NO_FULLSCREEN,
                    initialState: {
                        model: loadingScreenModel,
                    },
                    ariaLabelledBy: 'loading-screen-modal-title',
                });

                return loadingScreenModel.loadingScreenDisplayed$.pipe(
                    take(1),
                    switchMap(() => {
                        return this.docBoxService.getDocBoxFiles(materialNumber, serialNumber).pipe(
                            tap(() => loadingScreenInstance.hide()),
                            mergeMap((response) => {
                                return [getDocBoxSuccess({ notificationId: notificationId, docBoxData: response })];
                            }),
                            catchError((error) => {
                                this.log.error(
                                    `DocBoxEffects: getDocBox: Error while loading docbox list ${JSON.stringify(
                                        error,
                                        Object.getOwnPropertyNames(error),
                                    )}`,
                                );
                                loadingScreenInstance.hide();
                                return of(getDocBoxError());
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    public getDocBoxFromSideMenu$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getDocBoxFromSideMenu),
            switchMap((action) => {
                const { materialNumber, serialNumber, technicalIdentNo, functionalLocation } = action;
                return this.docBoxService
                    .getDocBoxFiles(materialNumber, serialNumber, technicalIdentNo, functionalLocation)
                    .pipe(
                        mergeMap((response) => {
                            return [getDocBoxFromSideMenuSuccess({ docBoxData: response })];
                        }),
                        catchError((error) => {
                            this.log.error(
                                `DocBoxEffects: getDocBoxSideMenu: Error while fetching side menu docbox list ${JSON.stringify(
                                    error,
                                    Object.getOwnPropertyNames(error),
                                )}`,
                            );
                            return of(getDocBoxError());
                        }),
                    );
            }),
        ),
    );

    public getDocBoxSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getDocBoxSuccess),
            switchMap((action) => {
                return of(navigateToDocBox({ notificationId: action.notificationId }));
            }),
        ),
    );

    public downloadDocuments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadDocuments),
            switchMap((action) => {
                return this.docBoxService.getDocument(action.docBoxIds).pipe(
                    tap((response) => {
                        this.docBoxService.saveDocument(response[0]);
                    }),
                    mergeMap(() => of(downloadDocumentsLoadingStatus({ docBoxId: action.docBoxIds }))),
                    catchError((error) => {
                        this.log.error(
                            `DocBoxEffects: downloadDocuments$: Error while downloading docbox list ${JSON.stringify(
                                error,
                                Object.getOwnPropertyNames(error),
                            )}`,
                        );
                        this.appNotificationService.setAppNotification(
                            AppNotificationType.WARN,
                            <string>this.translate.instant('DOCBOX_DOWNLOAD_FAILED_TITLE'),
                            <string>this.translate.instant('DOCBOX_DOWNLOAD_FAILED_BODY'),
                        );
                        return of(downloadDocumentsLoadingStatus({ docBoxId: action.docBoxIds }));
                    }),
                );
            }),
        ),
    );
}
